//@flow
import {useState, useCallback, useEffect} from 'react';
import {getProductOptionGroupSettingsService} from '../../product-option/promises';
import {useToastActions} from '../../toast-alert/utils';

import type {ProductOptionSetting} from '../../../entities/product';

type UseProductOptionSettingReturn = {
  productOptionSetting: ProductOptionSetting | null,
  loading: boolean,
  error: string | null,
  fetchProductOptionSetting: (productId: string) => Promise<void>,
};

export function useProductOptionSetting(
  productId?: string
): UseProductOptionSettingReturn {
  const [
    productOptionSetting,
    setProductOptionSetting,
  ] = useState<ProductOptionSetting | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const {showToast} = useToastActions();

  const fetchProductOptionSetting = useCallback(
    async (productId: string) => {
      if (!productId) {
        setError('El ID del producto es requerido');
        showToast({
          type: 'danger',
          title: 'Error de validación',
          message:
            'El ID del producto es requerido para cargar los ajustes de las opciones',
        });
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const settings = await getProductOptionGroupSettingsService(productId);
        setProductOptionSetting(settings);
      } catch (err) {
        if (err.status === 404 || err.status === 403) {
          return;
        }
        console.log(`Error fetching product option setting: ${err}`);
        const errorMessage =
          err?.message ||
          'No se pudo cargar los ajustes de las opciones del producto';
        setError(errorMessage);
        setProductOptionSetting(null);

        showToast({
          type: 'danger',
          title: 'Error',
          message: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [showToast]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      clearError();
    }, 5000);
    return () => clearTimeout(id);
  }, [clearError]);

  useEffect(() => {
    if (productId && productId !== 'new') {
      fetchProductOptionSetting(productId);
    }
  }, [productId, fetchProductOptionSetting]);

  return {
    productOptionSetting,
    loading,
    error,
    fetchProductOptionSetting,
  };
}
